import { Injectable, signal, type Signal, computed, inject } from "@angular/core";
import { WkDataTableStoreService } from "@components/data-table/store";
import { computedEqual } from "@shared/utils";
import type { WkDataTableInternalData, WkDataTableSearchIndexItems } from "@components/data-table/data-table.types";
import { WkDataTableContextService } from "@components/data-table/data-table-context.service";

@Injectable()
export class WkDataTableSearchService<TItem> {
    private readonly dataTableStore = inject(WkDataTableStoreService);
    private readonly dataTableContext = inject<WkDataTableContextService<TItem>>(WkDataTableContextService);

    public searchValue = signal<string>("");

    public readonly columns = computed(() => this.dataTableStore.visibleColumns().filter((c) => c.definition.searchable ?? false));

    private searchIndex = computed<WkDataTableSearchIndexItems<TItem>>(
        () => {
            const items = this.dataTableContext.data?.() ?? [];
            return items.map((item) => ({
                item,
                search: item.$searchCache ?? [],
            }));
        },
        { equal: computedEqual },
    );

    public applySearch(data: Signal<WkDataTableInternalData<TItem>>): Signal<WkDataTableInternalData<TItem>> {
        return computed(
            () => {
                const items = data();
                const searchValue = this.normalize(this.searchValue());
                const filteredIndex = this.searchIndex();
                if (!items.length || !searchValue || !filteredIndex.length) return items;

                const searchIfMatch = searchValue.length < 3;
                return filteredIndex.reduce<WkDataTableInternalData<TItem>>((acc, i) => {
                    const match = i.search.some((s) => {
                        if (s.exactMatch || searchIfMatch) return s.value === searchValue;

                        return s.value.includes(searchValue);
                    });

                    if (match) acc.push(i.item);

                    return acc;
                }, []);
            },
            { equal: computedEqual },
        );
    }

    public normalize(v: string): string {
        return v
            .normalize("NFD")
            .replace(/\p{Diacritic}/gu, "")
            .trim()
            .toLowerCase();
    }
}
